// frontend/src/pages/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import {
  TrendingUp, AlertTriangle, DollarSign, Users,
  Clock, CheckCircle, XCircle, Settings, RefreshCw,
  Download, Bell, Activity, Ban, ListChecks,
  BarChart3, LogOut, Sparkles
} from 'lucide-react';
import { getStatusConfig } from '../utils/settlementUtils';
import { formatNaira } from '../utils/money';

// Supporting Components
const MetricCard = ({ title, value, status, icon, trend, onClick }) => {
  const getStatusColor = () => {
    switch(status) {
      case 'healthy': return 'border-green-500/30 bg-green-500/5';
      case 'warning': return 'border-yellow-500/30 bg-yellow-500/5';
      case 'error': return 'border-red-500/30 bg-red-500/5';
      default: return 'border-gray-500/30 bg-gray-500/5';
    }
  };


  return (
    <div 
      className={`p-6 rounded-lg border ${getStatusColor()} cursor-pointer transition-all hover:scale-105`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-gray-400 text-sm font-medium">{title}</div>
        {icon && <div className="text-gray-400">{icon}</div>}
      </div>
      <div className="text-2xl font-bold text-white mb-1">{value || '...'}</div>
      {trend && (
        <div className={`text-xs flex items-center gap-1 ${
          trend.direction === 'up' ? 'text-green-400' : 
          trend.direction === 'down' ? 'text-red-400' : 'text-gray-400'
        }`}>
          <TrendingUp size={12} className={trend.direction === 'down' ? 'rotate-180' : ''} />
          {trend.value}% from yesterday
        </div>
      )}
    </div>
  );
};

const AlertsPanel = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-600 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="text-yellow-500" size={20} />
        <h3 className="text-lg font-semibold text-white">System Alerts</h3>
      </div>
      <div className="space-y-3">
        {alerts.map((alert, index) => (
          <div 
            key={index}
            className={`p-3 rounded-lg border ${
              alert.severity === 'high' ? 'border-red-500/30 bg-red-500/10' :
              alert.severity === 'medium' ? 'border-yellow-500/30 bg-yellow-500/10' :
              'border-blue-500/30 bg-blue-500/10'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-white">{alert.message}</div>
                <div className="text-sm text-gray-400">{alert.timestamp}</div>
              </div>
              <AlertTriangle 
                size={16} 
                className={
                  alert.severity === 'high' ? 'text-red-500' :
                  alert.severity === 'medium' ? 'text-yellow-500' :
                  'text-blue-500'
                }
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BetSourceAnalytics = ({ data }) => {
  if (!data || data.length === 0) return null;
  const label = (s) => (s === 'Copilot' ? 'Playmaker (AI)' : 'Manual');
  const Stat = ({ label, value }) => (
    <div><span className="text-gray-400">{label}:</span> <span className="text-white">{value}</span></div>
  );
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-600 p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={18} className="text-purple-300" />
        <h3 className="text-lg font-semibold text-white">Bet Source — Playmaker vs Manual</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {data.map((s) => (
          <div key={s.source} className="bg-gray-700/40 rounded-lg p-4 border border-gray-600">
            <div className="font-semibold text-white mb-3">{label(s.source)}</div>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <Stat label="Bets" value={s.total} />
              <Stat label="Win rate" value={s.decided > 0 ? `${s.winRate}%` : '—'} />
              <Stat label="Staked" value={formatNaira(s.staked)} />
              <Stat label="Won" value={`${s.wins}/${s.decided}`} />
              <Stat label="Pending" value={s.pending} />
              <Stat label="Avg stake" value={formatNaira(s.avgStake)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RecentBetsTable = ({ bets, onRefresh, onReGrade, onSettle, onVoid, onSearch }) => {
  const [q, setQ] = useState('');
  const runSearch = () => (onSearch ? onSearch(q.trim()) : onRefresh());
  return (
  <div className="bg-gray-800 rounded-lg border border-gray-600">
    <div className="p-6 border-b border-gray-600">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-white whitespace-nowrap">Recent Bets</h3>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            placeholder="Search Bet ID / user…"
            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 w-56"
          />
          <button
            onClick={() => { setQ(''); onSearch ? onSearch('') : onRefresh(); }}
            title="Clear / refresh"
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Bet ID
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              User
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              Settlement
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-600">
          {bets.map((bet) => (
            <tr key={bet.id} className="hover:bg-gray-700/50">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                <div className="font-mono">{bet.reference || `#${bet.id}`}</div>
                {bet.source === 'Copilot' && (
                  <div className="text-[10px] text-purple-300 mt-0.5">Playmaker</div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                {bet.user?.name || `User ${bet.userId}`}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  bet.type === 'BeastMode' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                }`}>
                  {bet.type === 'BeastMode' ? 'Beast Mode' : 'Safety Play'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                {formatNaira(bet.amount)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  {getStatusConfig(bet.status).icon}
                  <span className={`text-sm ${getStatusConfig(bet.status).color}`}>
                    {bet.status.replace('_', ' ')}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {bet.settlement?.canSettle ? (
                  <span className="text-green-400">Ready</span>
                ) : bet.settlement?.pendingPicks > 0 ? (
                  <span className="text-yellow-400">{bet.settlement.pendingPicks} pending</span>
                ) : (
                  <span className="text-gray-400">Complete</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                {/* A bet that's already paid out (Won/Partially_Won) or finalised
                    (Voided/Cancelled) can't be re-settled - hide the actions. */}
                {!['Won', 'Partially_Won', 'Voided', 'Cancelled'].includes(bet.status) && (
                  <div className="inline-flex items-center gap-2">
                    {onReGrade && (
                      <button
                        onClick={() => onReGrade(bet.id)}
                        title="Reset to Pending and re-check the real result"
                        className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-md hover:bg-blue-500/20 transition-colors"
                      >
                        <RefreshCw size={12} />
                        Re-grade
                      </button>
                    )}
                    {onSettle && (
                      <button
                        onClick={() => onSettle(bet.id)}
                        title="Manually settle as won, lost or partial win"
                        className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-green-300 bg-green-500/10 border border-green-500/30 rounded-md hover:bg-green-500/20 transition-colors"
                      >
                        <CheckCircle size={12} />
                        Settle
                      </button>
                    )}
                    {onVoid && (
                      <button
                        onClick={() => onVoid(bet.id)}
                        title="Void the bet and refund the stake"
                        className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-300 bg-red-500/10 border border-red-500/30 rounded-md hover:bg-red-500/20 transition-colors"
                      >
                        <XCircle size={12} />
                        Void
                      </button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};

// PickStatus values the backend expects: 2 = Won, 3 = Lost, 4 = Voided
const PICK_WON = 2;
const PICK_LOST = 3;
const PICK_VOID = 4;

// Derive the bet-level outcome from the chosen per-pick results, mirroring the
// backend (voided legs drop out; SafePlay insurance on one miss for 3+ legs).
const derivePreview = (betType, results) => {
  const values = Object.values(results);
  if (values.some((v) => v == null)) return { label: 'Set every pick', tone: 'text-gray-400' };
  const active = values.filter((v) => v !== PICK_VOID);
  if (active.length === 0) return { label: 'Void — stake refunded', tone: 'text-gray-300' };
  const wonCount = active.filter((v) => v === PICK_WON).length;
  const allWon = wonCount === active.length;
  if (betType === 'BeastMode') {
    return allWon
      ? { label: 'Won — full payout', tone: 'text-green-400' }
      : { label: 'Lost — no payout', tone: 'text-red-400' };
  }
  if (allWon) return { label: 'Won — full payout', tone: 'text-green-400' };
  if (active.length >= 3 && wonCount === active.length - 1)
    return { label: 'Partial Win — insurance payout', tone: 'text-blue-400' };
  return { label: 'Lost — no payout', tone: 'text-red-400' };
};

const PickSettlementModal = ({ bet, onClose, onSubmit }) => {
  const initial = {};
  (bet.picks || []).forEach((p) => {
    const s = (p.status || '').toLowerCase();
    initial[p.id] = s === 'won' ? PICK_WON : s === 'lost' ? PICK_LOST : s === 'voided' ? PICK_VOID : null;
  });
  const [results, setResults] = useState(initial);
  const [reason, setReason] = useState('Graded manually from official result');
  const [submitting, setSubmitting] = useState(false);

  const picks = bet.picks || [];
  const allSet = picks.length > 0 && picks.every((p) => results[p.id] != null);
  const preview = derivePreview(bet.type, results);

  const setPick = (pickId, value) => setResults((prev) => ({ ...prev, [pickId]: value }));

  const PickButton = ({ pickId, value, label, activeClass, icon }) => (
    <button
      type="button"
      onClick={() => setPick(pickId, value)}
      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
        results[pickId] === value
          ? activeClass
          : 'text-gray-400 bg-gray-700/40 border-gray-600 hover:bg-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  const submit = async () => {
    if (!allSet || !reason.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(
        picks.map((p) => ({ pickId: p.id, status: results[p.id] })),
        reason.trim()
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-600">
          <div className="flex items-center gap-2">
            <ListChecks size={18} className="text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Settle bet #{bet.id} by pick</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XCircle size={20} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <p className="text-xs text-gray-400">
            Set the real result for each leg. Voided legs drop out of the parlay; the bet outcome and
            payout are calculated from the rest ({bet.type === 'BeastMode' ? 'Beast Mode — all must win' : 'Safety Play — insurance on one miss'}).
          </p>

          {picks.length === 0 ? (
            <div className="text-sm text-yellow-400">This bet has no picks to settle.</div>
          ) : (
            picks.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 p-3 bg-gray-900/40 rounded-md border border-gray-700">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">{p.playerName || p.teamName || `Pick ${p.id}`}</div>
                  <div className="text-xs text-gray-400 truncate">
                    {p.statType} {(p.pickType || '').toUpperCase()} {p.line}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <PickButton pickId={p.id} value={PICK_WON} label="Won" icon={<CheckCircle size={12} />}
                    activeClass="text-green-300 bg-green-500/20 border-green-500/40" />
                  <PickButton pickId={p.id} value={PICK_LOST} label="Lost" icon={<XCircle size={12} />}
                    activeClass="text-red-300 bg-red-500/20 border-red-500/40" />
                  <PickButton pickId={p.id} value={PICK_VOID} label="Void" icon={<Ban size={12} />}
                    activeClass="text-gray-200 bg-gray-500/30 border-gray-400/40" />
                </div>
              </div>
            ))
          )}

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-gray-400">Outcome preview</span>
            <span className={`text-sm font-semibold ${preview.tone}`}>{preview.label}</span>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-900 border border-gray-600 rounded-md text-white focus:outline-none focus:border-blue-500"
              placeholder="Why is this being settled?"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-600">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:text-white">Cancel</button>
          <button
            onClick={submit}
            disabled={!allSet || !reason.trim() || submitting}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCircle size={14} />
            {submitting ? 'Settling…' : 'Settle bet'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = (props) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentBets, setRecentBets] = useState([]);
  const [betSources, setBetSources] = useState([]);
  const [pickSettleBet, setPickSettleBet] = useState(null);
  const { onLogout } = props;


  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const getAuthHeaders = () => {
    // Admin endpoints require an admin JWT
    const token = localStorage.getItem('playerbet_admin_token') || localStorage.getItem('playerbet_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const ADMIN_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const authHeaders = getAuthHeaders();

      // Fetch real dashboard metrics and system health in parallel
      const [dashboardResponse, healthResponse] = await Promise.all([
        fetch(`${ADMIN_BASE_URL}/admin/monitoring/dashboard`, { headers: authHeaders }),
        fetch(`${ADMIN_BASE_URL}/admin/monitoring/system-health`, { headers: authHeaders })
      ]);

      if (dashboardResponse.status === 401 || dashboardResponse.status === 403) {
        throw new Error('Admin access required - please log in with an admin account');
      }
      if (!dashboardResponse.ok) {
        throw new Error(`Dashboard request failed (HTTP ${dashboardResponse.status})`);
      }

      const data = await dashboardResponse.json();
      const healthData = healthResponse.ok ? await healthResponse.json() : null;

      const dashboardData = {
        systemStatus: {
          systemHealth: data.systemStatus?.systemHealth === 'Healthy' ? 'healthy' : 'error',
          pendingBets: data.systemStatus?.pendingBets ?? 0,
          pendingSettlements: data.systemStatus?.pendingSettlements ?? 0,
          stuckSettlements: data.systemStatus?.stuckSettlements ?? 0,
          apiHealth: healthData?.components?.externalApis === 'healthy',
          dbHealth: healthData?.components?.database === 'healthy' ? 'healthy' : 'unknown'
        },
        todayStats: {
          totalVolume: data.todayStats?.totalVolume ?? 0,
          settlementSuccessRate: Number(data.todayStats?.settlementSuccessRate ?? 0),
          totalCommissions: data.todayStats?.totalCommissions ?? 0,
          totalBets: data.todayStats?.totalBets ?? 0,
          totalUsers: data.todayStats?.totalUsers ?? 0
        },
        platformStats: {
          totalVolume: data.platformStats?.totalVolume ?? 0,
          totalPaidOut: data.platformStats?.totalPaidOut ?? 0,
          netRevenue: data.platformStats?.netRevenue ?? 0,
          totalBets: data.platformStats?.totalBets ?? 0,
          totalUsers: data.platformStats?.totalUsers ?? 0,
          settlementRate: Number(data.platformStats?.settlementRate ?? 0)
        },
        alerts: [
          ...(data.alerts || []),
          ...(healthData && healthData.status !== 'healthy' ? [{
            message: `System health degraded: ${(healthData.issues || []).join(', ')}`,
            severity: 'high',
            timestamp: new Date().toLocaleString()
          }] : [])
        ]
      };

      setDashboard(dashboardData);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBetSourceAnalytics = async () => {
    try {
      const response = await fetch(`${ADMIN_BASE_URL}/admin/monitoring/bet-source-analytics`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) return;
      const data = await response.json();
      setBetSources(data.sources || []);
    } catch (err) {
      console.error('Error fetching bet source analytics:', err);
    }
  };

  const fetchRecentBets = async (search = '') => {
    try {
      const qs = `limit=25${search ? `&search=${encodeURIComponent(search)}` : ''}`;
      const response = await fetch(`${ADMIN_BASE_URL}/admin/monitoring/recent-bets?${qs}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Recent bets request failed (HTTP ${response.status})`);
      }

      const data = await response.json();
      setRecentBets(data.bets || []);
    } catch (err) {
      console.error('Error fetching recent bets:', err);
    }
  };

  // Reset a bet to Pending and re-run verification against the real sources.
  // Used to fix bets that were graded wrongly (e.g. on mock data).
  const handleReGrade = async (betId) => {
    const reason = window.prompt(
      `Re-grade bet #${betId}?\n\nThis resets it to Pending and re-checks the real result. ` +
      `It will not touch bets that already paid out.\n\nReason:`,
      'Verified real stats; re-grade from source'
    );
    if (!reason) return;

    const adminUserId = Number(localStorage.getItem('playerbet_admin_user_id') || 0);
    if (!adminUserId) {
      alert('Could not determine your admin user id - please log out and back in.');
      return;
    }

    try {
      const response = await fetch(`${ADMIN_BASE_URL}/admin/settlement/re-grade`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ betId, reason, adminUserId })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || `Re-grade failed (HTTP ${response.status})`);
      }
      alert(result.message || `Bet #${betId} re-graded: ${result.status}`);
      fetchRecentBets();
      fetchDashboard();
    } catch (err) {
      alert(err.message || 'Re-grade failed');
    }
  };

  // Open the per-pick settlement modal for a bet (leg-accurate settling).
  const openPickSettle = (betId) => {
    const bet = recentBets.find((b) => b.id === betId);
    if (!bet) {
      alert('Could not find that bet - refresh and try again.');
      return;
    }
    setPickSettleBet(bet);
  };

  // Submit per-pick results; the backend derives the bet outcome + payout.
  const handlePickSettleSubmit = async (picks, reason) => {
    if (!pickSettleBet) return;
    const adminUserId = Number(localStorage.getItem('playerbet_admin_user_id') || 0);
    if (!adminUserId) {
      alert('Could not determine your admin user id - please log out and back in.');
      return;
    }
    try {
      const response = await fetch(`${ADMIN_BASE_URL}/admin/settlement/settle-picks`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ betId: pickSettleBet.id, picks, reason, adminUserId })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || `Settle failed (HTTP ${response.status})`);
      }
      alert(result.message || `Bet #${pickSettleBet.id} settled.`);
      setPickSettleBet(null);
      fetchRecentBets();
      fetchDashboard();
    } catch (err) {
      alert(err.message || 'Per-pick settle failed');
    }
  };

  // Void a bet and refund the full stake to the user. Used when a result
  // can't be confirmed and the fair outcome is to give the money back.
  const handleVoid = async (betId) => {
    const reason = window.prompt(
      `Void bet #${betId}?\n\nThis refunds the full stake to the user and cannot be undone. ` +
      `It will not touch bets that already paid out.\n\nReason:`,
      'Result could not be confirmed; stake refunded'
    );
    if (!reason) return;

    const adminUserId = Number(localStorage.getItem('playerbet_admin_user_id') || 0);
    if (!adminUserId) {
      alert('Could not determine your admin user id - please log out and back in.');
      return;
    }

    try {
      const response = await fetch(`${ADMIN_BASE_URL}/admin/settlement/void-bet`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ betId, reason, adminUserId })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || `Void failed (HTTP ${response.status})`);
      }
      alert(result.message || `Bet #${betId} voided and refunded.`);
      fetchRecentBets();
      fetchDashboard();
    } catch (err) {
      alert(err.message || 'Void failed');
    }
  };

  // Download the audit-log CSV for the last 90 days.
  const exportAuditLogs = async () => {
    try {
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 90);

      const response = await fetch(`${ADMIN_BASE_URL}/admin/monitoring/export/audit-logs`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromDate: fromDate.toISOString(), toDate: toDate.toISOString() })
      });
      if (!response.ok) {
        throw new Error(`Export failed (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${fromDate.toISOString().slice(0, 10)}_${toDate.toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Export failed');
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchRecentBets();
    fetchBetSourceAnalytics();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboard();
      fetchRecentBets();
      fetchBetSourceAnalytics();
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-gray-400">Loading admin dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <XCircle size={48} className="text-red-500 mx-auto mb-4" />
          <div className="text-xl font-semibold mb-2">Error Loading Dashboard</div>
          <div className="text-gray-400 mb-4">{error}</div>
          <button 
            onClick={fetchDashboard}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">PlayerBet Admin</h1>
            <p className="text-blue-100 opacity-90">System monitoring and management</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-2 h-2 rounded-full ${
                dashboard?.systemStatus.systemHealth === 'healthy' ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              System {dashboard?.systemStatus.systemHealth === 'healthy' ? 'Healthy' : 'Issues'}
            </div>
            <button 
              onClick={fetchDashboard}
              className="p-2 text-white/80 hover:text-white transition-colors"
            >
              <RefreshCw size={20} />
            </button>
                      <button
            onClick={onLogout}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px'
            }}
          >
            <LogOut size={16} />
            Logout
          </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* System Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Pending Bets"
            value={dashboard?.systemStatus.pendingBets}
            status={dashboard?.systemStatus.pendingBets > 30 ? 'warning' : 'healthy'}
            icon={<Clock size={20} />}
          />
          <MetricCard
            title="Total Volume Wagered"
            value={formatNaira(dashboard?.platformStats.totalVolume)}
            status="healthy"
            icon={<DollarSign size={20} />}
          />
          <MetricCard
            title="Settlement Rate"
            value={`${dashboard?.platformStats.settlementRate?.toFixed(1)}%`}
            status={(dashboard?.platformStats.settlementRate ?? 0) >= 95 ? 'healthy' : 'warning'}
            icon={<CheckCircle size={20} />}
          />
          <MetricCard
            title="Net Revenue (House)"
            value={formatNaira(dashboard?.platformStats.netRevenue)}
            status="healthy"
            icon={<TrendingUp size={20} />}
          />
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard
            title="Total Bets"
            value={dashboard?.platformStats.totalBets}
            status="healthy"
            icon={<BarChart3 size={20} />}
          />
          <MetricCard
            title="Total Users"
            value={dashboard?.platformStats.totalUsers}
            status="healthy"
            icon={<Users size={20} />}
          />
          <MetricCard
            title="Total Paid Out"
            value={formatNaira(dashboard?.platformStats.totalPaidOut)}
            status="healthy"
            icon={<DollarSign size={20} />}
          />
        </div>

        {/* Alerts Panel */}
        {dashboard?.alerts?.length > 0 && (
          <AlertsPanel alerts={dashboard.alerts} />
        )}

        {/* Bet Source Analytics */}
        <BetSourceAnalytics data={betSources} />

        {/* Recent Bets Table */}
        <RecentBetsTable
          bets={recentBets}
          onRefresh={fetchRecentBets}
          onSearch={fetchRecentBets}
          onReGrade={handleReGrade}
          onSettle={openPickSettle}
          onVoid={handleVoid}
        />

        {pickSettleBet && (
          <PickSettlementModal
            bet={pickSettleBet}
            onClose={() => setPickSettleBet(null)}
            onSubmit={handlePickSettleSubmit}
          />
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={exportAuditLogs}
            className="p-4 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors text-left"
          >
            <Download size={20} className="text-blue-400 mb-2" />
            <div className="text-sm font-medium">Export Audit Logs</div>
            <div className="text-xs text-gray-400 mt-1">Download CSV (last 90 days)</div>
          </button>
          {[
            { icon: <Settings size={20} />, label: 'System Settings' },
            { icon: <Activity size={20} />, label: 'View Logs' },
            { icon: <Users size={20} />, label: 'User Management' }
          ].map((action) => (
            <button
              key={action.label}
              disabled
              title="Coming soon"
              className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg text-left opacity-60 cursor-not-allowed relative"
            >
              <span className="text-gray-500 mb-2 block">{action.icon}</span>
              <div className="text-sm font-medium text-gray-400">{action.label}</div>
              <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wide text-gray-500 border border-gray-600 rounded px-1.5 py-0.5">
                Soon
              </span>
            </button>
          ))}
        </div>

        {/* System Information */}
        <div className="bg-gray-800 rounded-lg border border-gray-600 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">System Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-2">API Health</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-300">Sports Data API</span>
                  <span className={`text-sm ${
                    dashboard?.systemStatus.apiHealth ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {dashboard?.systemStatus.apiHealth ? 'Healthy' : 'Down'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-300">Database</span>
                  <span className="text-sm text-green-400">Healthy</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-300">Cache Service</span>
                  <span className="text-sm text-green-400">Operational</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-2">Performance</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-300">Avg Response Time</span>
                  <span className="text-sm text-white">45ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-300">Memory Usage</span>
                  <span className="text-sm text-white">67%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-300">CPU Usage</span>
                  <span className="text-sm text-white">23%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;